import {
	abbreviationCadences,
	type AbbreviatedFrequencyModel,
	type FrequencyDetailsGroupedByCadenceModel,
	type FrequencyDetailsModel,
	type FrequencyDetailsWithCadenceModel
} from './types';
import { useAgencyStartOfWeek } from '~/libraries/agencies/queries';
import { DisciplineOptions, FrequencyCadenceTypeEnum, FrequencyCadenceTypeOptions, FrequencyTypeEnum } from '@generated';
import { addDays, endOfDay, endOfMonth, startOfDay, startOfMonth } from 'date-fns';
import { getPreviousOccurenceOf } from '~/util/date';
import { getCadenceCorrelator } from '../util';

function getAbbreviationLabel(frequency: {
	frequencyTypeId: number;
	frequencyCadenceTypeId: number;
	frequencyCount: number;
	frequencyCadenceCount: number;
}) {
	if (frequency.frequencyTypeId === FrequencyTypeEnum.Evaluation) {
		return '1x';
	}
	const frequencyCadence = FrequencyCadenceTypeOptions.find(c => c.id === frequency.frequencyCadenceTypeId);
	if (!frequencyCadence) {
		return `${frequency.frequencyCount} ? ${frequency.frequencyCadenceCount}`;
	}
	return `${frequency.frequencyCount}${frequencyCadence.label[0]}${frequency.frequencyCadenceCount}`;
}

function abbreviateEvaluations(evaluations: FrequencyDetailsModel[]): AbbreviatedFrequencyModel[] {
	return evaluations.map(evalFrequency => {
		const discipline = DisciplineOptions.find(d => d.id === evalFrequency.disciplineId)!;
		return {
			id: evalFrequency.id,
			disciplineId: evalFrequency.disciplineId,
			frequencyCadenceTypeId: evalFrequency.frequencyCadenceTypeId,
			frequencyTypeId: evalFrequency.frequencyTypeId,
			frequencyStartDate: evalFrequency.scheduledDateAndTime,
			frequencyEndDate: evalFrequency.scheduledDateAndTime,
			frequencies: [evalFrequency],
			frequencyCount: 1,
			frequencyCadenceCount: 1,
			disciplineLabel: discipline.label,
			disciplineShortLabel: discipline.abbreviation,
			abbreviationLabel: getAbbreviationLabel({
				frequencyTypeId: evalFrequency.frequencyTypeId,
				frequencyCadenceTypeId: evalFrequency.frequencyCadenceTypeId,
				frequencyCount: 1,
				frequencyCadenceCount: 1
			})
		};
	});
}

function groupByCadence(
	frequencies: FrequencyDetailsWithCadenceModel[],
	certificationPeriod: { startDate: Date; endDate: Date },
	agencyStartOfWeek: number
): FrequencyDetailsGroupedByCadenceModel[] {
	const groupedFrequencies: FrequencyDetailsGroupedByCadenceModel[] = [];

	frequencies.forEach(frequency => {
		const matchingGroup = groupedFrequencies.find(
			group =>
				group.frequencyCadenceTypeId === frequency.frequencyCadenceTypeId &&
				group.dayWeekOrMonthNumber === frequency.dayWeekOrMonthNumber &&
				group.disciplineId === frequency.disciplineId &&
				group.frequencyTypeId === frequency.frequencyTypeId
		);
		if (matchingGroup) {
			matchingGroup.frequencies.push(frequency);
		} else {
			const frequencyDates = calculateFrequencyStartAndEndDates(
				frequency.scheduledDateAndTime,
				frequency.frequencyCadenceTypeId,
				certificationPeriod.startDate,
				certificationPeriod.endDate,
				agencyStartOfWeek
			);
			groupedFrequencies.push({
				disciplineId: frequency.disciplineId,
				frequencyCadenceTypeId: frequency.frequencyCadenceTypeId,
				frequencyTypeId: frequency.frequencyTypeId,
				dayWeekOrMonthNumber: frequency.dayWeekOrMonthNumber,
				frequencyStartDate: frequencyDates.frequencyStartDate,
				frequencyEndDate: frequencyDates.frequencyEndDate,
				frequencies: [frequency]
			});
		}
	});

	return groupedFrequencies;
}

function calculateFrequencyStartAndEndDates(
	frequencyDate: Date,
	frequencyCadenceId: number,
	certificationStateDate: Date,
	certificationPeriodEndDate: Date,
	agencyStartOfWeek: number
) {
	if (frequencyCadenceId === FrequencyCadenceTypeEnum.Daily) {
		return {
			frequencyStartDate: startOfDay(frequencyDate),
			frequencyEndDate: endOfDay(frequencyDate)
		};
	} else if (frequencyCadenceId === FrequencyCadenceTypeEnum.Weekly) {
		const frequencyStartDate = getPreviousOccurenceOf(frequencyDate, agencyStartOfWeek);
		const frequencyEndDate =
			addDays(frequencyStartDate, 6) > certificationPeriodEndDate ? certificationPeriodEndDate : addDays(frequencyStartDate, 6);
		return {
			frequencyStartDate: frequencyStartDate < certificationStateDate ? certificationStateDate : frequencyStartDate,
			frequencyEndDate: frequencyEndDate
		};
	} else if (frequencyCadenceId === FrequencyCadenceTypeEnum.Monthly) {
		const frequencyStartDate = startOfMonth(frequencyDate);
		const frequencyEndDate = endOfMonth(frequencyDate);
		return {
			frequencyStartDate: frequencyStartDate < certificationStateDate ? certificationStateDate : frequencyStartDate,
			frequencyEndDate: frequencyEndDate
		};
	}
	throw new Error('Invalid frequency cadence type');
}

function combineGroupedFrequencies(frequenciesGroupedByCadences: FrequencyDetailsGroupedByCadenceModel[]): AbbreviatedFrequencyModel[] {
	if (frequenciesGroupedByCadences.length === 0) {
		return [];
	}

	const disciplines = new Set([...frequenciesGroupedByCadences.map(gf => gf.disciplineId)]);
	let result: AbbreviatedFrequencyModel[] = [];

	for (const disciplineId of disciplines) {
		const selectedDiscipline = DisciplineOptions.find(d => d.id === disciplineId)!;

		const disciplineFrequencies = frequenciesGroupedByCadences
			.filter(gf => gf.disciplineId === disciplineId)
			.sort((a, b) => a.dayWeekOrMonthNumber - b.dayWeekOrMonthNumber);

		const firstFrequency = disciplineFrequencies[0];

		const frequencyAbbreviation: (AbbreviatedFrequencyModel & { currentDayWeekOrMonthNumber: number })[] = [
			{
				id: window.crypto.randomUUID(),
				...firstFrequency,
				frequencyCount: firstFrequency.frequencies.length,
				frequencies: firstFrequency.frequencies,
				currentDayWeekOrMonthNumber: firstFrequency.dayWeekOrMonthNumber,
				frequencyCadenceCount: 1,
				disciplineLabel: selectedDiscipline.label,
				disciplineShortLabel: selectedDiscipline.abbreviation,
				abbreviationLabel: getAbbreviationLabel({
					frequencyTypeId: firstFrequency.frequencyTypeId,
					frequencyCadenceTypeId: firstFrequency.frequencyCadenceTypeId,
					frequencyCount: firstFrequency.frequencies.length,
					frequencyCadenceCount: 1
				})
			}
		];

		if (disciplineFrequencies.length === 1) {
			result = result.concat(frequencyAbbreviation);
			continue;
		}

		for (let i = 1; i < disciplineFrequencies.length; i++) {
			const currentFrequency = disciplineFrequencies[i];
			const lastFrequencyAdded = frequencyAbbreviation[frequencyAbbreviation.length - 1];
			if (
				//if frequency is next consecutive cadence
				currentFrequency.dayWeekOrMonthNumber - lastFrequencyAdded.currentDayWeekOrMonthNumber === 1 &&
				//if has the same number of visits
				currentFrequency.frequencies.length === lastFrequencyAdded.frequencyCount
			) {
				lastFrequencyAdded.frequencyEndDate = currentFrequency.frequencyEndDate;
				lastFrequencyAdded.frequencyCadenceCount++;
				lastFrequencyAdded.currentDayWeekOrMonthNumber = currentFrequency.dayWeekOrMonthNumber;
				lastFrequencyAdded.frequencies = lastFrequencyAdded.frequencies.concat(currentFrequency.frequencies);
				lastFrequencyAdded.abbreviationLabel = getAbbreviationLabel({
					frequencyTypeId: lastFrequencyAdded.frequencyTypeId,
					frequencyCadenceTypeId: lastFrequencyAdded.frequencyCadenceTypeId,
					frequencyCount: lastFrequencyAdded.frequencyCount,
					frequencyCadenceCount: lastFrequencyAdded.frequencyCadenceCount
				});
			} else {
				frequencyAbbreviation.push({
					id: window.crypto.randomUUID(),
					...currentFrequency,
					frequencyCount: currentFrequency.frequencies.length,
					frequencies: currentFrequency.frequencies,
					currentDayWeekOrMonthNumber: currentFrequency.dayWeekOrMonthNumber,
					frequencyCadenceCount: 1,
					disciplineLabel: selectedDiscipline.label,
					disciplineShortLabel: selectedDiscipline.abbreviation,
					abbreviationLabel: getAbbreviationLabel({
						frequencyTypeId: currentFrequency.frequencyTypeId,
						frequencyCadenceTypeId: currentFrequency.frequencyCadenceTypeId,
						frequencyCount: currentFrequency.frequencies.length,
						frequencyCadenceCount: 1
					})
				});
			}
		}

		result = result.concat(frequencyAbbreviation);
	}
	return result;
}

export function getFrequencyAbbreviationText({
	frequencyTypeId,
	frequencyCadenceTypeId,
	frequencyCount,
	frequencyCadenceCount
}: {
	frequencyTypeId: number;
	frequencyCadenceTypeId: number;
	frequencyCount: number;
	frequencyCadenceCount: number;
}): string {
	if (frequencyTypeId === FrequencyTypeEnum.Evaluation) {
		return '';
	}
	const frequencyCadence = FrequencyCadenceTypeOptions.find(c => c.id === frequencyCadenceTypeId);
	if (!frequencyCadence) {
		return `${frequencyCount} ? ${frequencyCadenceCount}`;
	}
	return `${frequencyCount}${frequencyCadence.label[0]}${frequencyCadenceCount}`;
}

export function getFrequencyAbbreviationDateSpan({
	frequencyTypeId,
	frequencyStartDate,
	frequencyEndDate
}: {
	frequencyTypeId: number;
	frequencyStartDate: Date;
	frequencyEndDate: Date;
}) {
	if (frequencyTypeId === FrequencyTypeEnum.Evaluation) {
		return frequencyStartDate.toLocaleDateString();
	}
	return `${frequencyStartDate.toLocaleDateString()} - ${frequencyEndDate.toLocaleDateString()}`;
}

export function abbreviateFrequencies(
	frequencies: FrequencyDetailsModel[],
	certificationPeriod: { startDate: Date; endDate: Date },
	agencyStartOfWeek: number
): AbbreviatedFrequencyModel[] {
	const baseFrequencies: FrequencyDetailsWithCadenceModel[] = frequencies
		.sort((a, b) => a.scheduledDateAndTime.getTime() - b.scheduledDateAndTime.getTime())
		.map(visit => ({
			...visit,
			dayWeekOrMonthNumber: getCadenceCorrelator(visit.frequencyCadenceTypeId, visit.scheduledDateAndTime, agencyStartOfWeek)
		}));

	const abbreviatedFrequencies: AbbreviatedFrequencyModel[] = [];

	const frequencyTypeIds = new Set([...baseFrequencies.map(f => f.frequencyTypeId)]);
	for (const frequencyTypeId of frequencyTypeIds) {
		const frequenciesOfType = baseFrequencies.filter(f => f.frequencyTypeId === frequencyTypeId);
		if (frequencyTypeId === FrequencyTypeEnum.Evaluation) {
			abbreviatedFrequencies.push(...abbreviateEvaluations(frequenciesOfType));
			continue;
		}

		const groupedByCadence = groupByCadence(frequenciesOfType, certificationPeriod, agencyStartOfWeek);

		for (const cadence of abbreviationCadences) {
			const cadenceFrequencies = groupedByCadence
				.filter(x => x.frequencyCadenceTypeId === cadence)
				.sort((a, b) => a.dayWeekOrMonthNumber - b.dayWeekOrMonthNumber);
			abbreviatedFrequencies.push(...combineGroupedFrequencies(cadenceFrequencies));
		}
	}

	return abbreviatedFrequencies.sort((a, b) => {
		if (a.disciplineId !== b.disciplineId) {
			return a.disciplineId - b.disciplineId;
		} else if (a.frequencyTypeId !== b.frequencyTypeId) {
			return a.frequencyTypeId - b.frequencyTypeId;
		}
		return a.frequencyStartDate.getTime() - b.frequencyStartDate.getTime();
	});
}

export interface AbbreviateFrequenciesArguments {
	frequencies: FrequencyDetailsModel[];
	certificationPeriod: { startDate: Date; endDate: Date };
}

export type AbbreviateFrequenciesFunc = (
	frequencies: FrequencyDetailsModel[],
	certificationPeriod: { startDate: Date; endDate: Date }
) => AbbreviatedFrequencyModel[];

export function useAbbreviateFrequencies(): AbbreviateFrequenciesFunc {
	const agencyStartOfWeek = useAgencyStartOfWeek();
	return (frequencies: FrequencyDetailsModel[], certificationPeriod: { startDate: Date; endDate: Date }) =>
		abbreviateFrequencies(frequencies, certificationPeriod, agencyStartOfWeek);
}
